import {useConfig} from '@aboutyou/config';
import {
  AttributeFilterSection_AttributeValue,
  BrandFilterSection,
  BrandFilterSection_BrandGroup,
  BrandFilterSection_PerformingBrandsFilter,
  BrandFilterSection_PersonalizedBrandsFilter,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';
import {useTranslation} from '@aboutyou/i18n';
import {COLORS, HighlightBrand} from '@aboutyou/ui-components';
import {getAdjustedCdnUrl} from '@aboutyou/utils';
import {useCallback, useMemo} from 'react';
import {
  QuickFilterSlugs,
  useQuickFilter,
} from '../../../backend-services/CategoryStream/utils';

export const useBrandSection = ({
  data,
  resetFilters,
  setFilter,
  activeFilters,
}: {
  data: BrandFilterSection;
  activeFilters: Map<string, Array<string> | undefined>;
  setFilter?: (filterData: {
    filterId: string;
    filterSection: string;
    filterSlug?: string;
  }) => void;
  resetFilters?: (slug: string[]) => void;
}) => {
  const {additionalContent, brandGroups, slug} = data;
  const {device} = useConfig();

  const {t} = useTranslation();

  const {
    appliedQuickFilterKey,
    excludedBrands,
    personalizedBrandsFilterApplied,
    setQuickFilter,
  } = useQuickFilter();

  let highlightedBrandsFilter;
  let highlightedBrandsLabel = '';

  if (additionalContent?.$case === 'personalizedBrandsFilter') {
    highlightedBrandsFilter = additionalContent.personalizedBrandsFilter;
    highlightedBrandsLabel = t('Your favourites');
  }
  if (additionalContent?.$case === 'performingBrandsFilter') {
    highlightedBrandsFilter = additionalContent.performingBrandsFilter;
    highlightedBrandsLabel = t('Popular brands');
  }

  const highlightedBrandsType = additionalContent?.$case
    ? ({
        personalizedBrandsFilter: 'personalizedBrands',
        performingBrandsFilter: 'performingBrands',
      }[additionalContent?.$case] as
        | 'personalizedBrands'
        | 'performingBrands'
        | undefined)
    : undefined;

  const normalizedHighlightedBrands = useNormalizedHighlightedBrands(
    highlightedBrandsFilter,
  );

  const normalizedBrandGroups: NormalizedBrand[] = useNormalizedBrandGroups(
    brandGroups,
    additionalContent?.$case === 'personalizedBrandsFilter' &&
      device === 'mobile',
    highlightedBrandsLabel,
    normalizedHighlightedBrands,
  );

  const allBrandsIds = normalizedBrandGroups.reduce<string[]>(
    (acc, brandGroup) => acc.concat(brandGroup.items.map(({id}) => id)),
    [],
  );

  let activeSlugFilters = (activeFilters.get(slug) ?? []).filter(value =>
    allBrandsIds.includes(value),
  );

  /*
   * personalizedBrandsFilter case must be handled differently, as the selected favourite brands are NOT appended to the URL
   * instead they are all active unless they are specifically excluded
   */
  if (
    additionalContent?.$case === 'personalizedBrandsFilter' &&
    personalizedBrandsFilterApplied
  ) {
    const highlightedBrandsIds = normalizedHighlightedBrands.map(({id}) => id);

    // Remove excluded highlighted brands from highlighted brands -> array difference
    const activeHighlightedBrandsIds = highlightedBrandsIds.filter(
      id => !excludedBrands?.includes(id),
    );
    activeSlugFilters = activeSlugFilters.concat(activeHighlightedBrandsIds);
  }

  const favoriteBrandsFilterActive =
    appliedQuickFilterKey === QuickFilterSlugs.CUSTOMER;

  const handleAllFavoriteBrandsToggle = () => {
    if (appliedQuickFilterKey === QuickFilterSlugs.CUSTOMER) {
      setQuickFilter(QuickFilterSlugs.CUSTOMER, undefined);
    } else {
      setQuickFilter(QuickFilterSlugs.CUSTOMER, 'true');
    }
  };

  const handleSingleFavoriteBrandClick = (
    brandId: string,
    brandSlug?: string,
  ) => {
    if (highlightedBrandsType === 'performingBrands') {
      handlePerformingBrandClick(brandId, brandSlug);
    }
    if (highlightedBrandsType === 'personalizedBrands') {
      handlePersonalizedBrandClick(brandId);
    }
  };

  const handlePerformingBrandClick = (brandId: string, brandSlug?: string) => {
    setFilter?.({
      filterId: brandId,
      filterSection: slug,
      filterSlug: brandSlug,
    });
  };

  const handlePersonalizedBrandClick = (brandId: string) => {
    const currentExcludedBrands = excludedBrands ?? [];

    if (favoriteBrandsFilterActive) {
      if (currentExcludedBrands.includes(brandId)) {
        const updatedExcludedBrands = currentExcludedBrands.filter(
          bId => bId !== brandId,
        );

        setQuickFilter(QuickFilterSlugs.CUSTOMER, 'true', {
          excludedBrands: updatedExcludedBrands,
          favouriteBrands: [...activeSlugFilters, brandId],
        });
      } else {
        const updatedExcludedBrands = [...currentExcludedBrands, brandId];

        const areAllSelectedBrandsExcluded =
          normalizedHighlightedBrands.length === updatedExcludedBrands.length;

        if (areAllSelectedBrandsExcluded) {
          setQuickFilter(QuickFilterSlugs.CUSTOMER, undefined, {
            favouriteBrands: [...activeSlugFilters, brandId],
          });
        } else {
          setQuickFilter(QuickFilterSlugs.CUSTOMER, 'true', {
            excludedBrands: updatedExcludedBrands,
            favouriteBrands: activeSlugFilters.filter(item => item !== brandId),
          });
        }
      }
    } else {
      const updatedExcludedBrands = normalizedHighlightedBrands
        .filter(brand => brand.id !== brandId)
        .map(brand => brand.id);
      const activeFavoriteBrands = normalizedHighlightedBrands
        .filter(brand => brand.id === brandId)
        .map(brand => brand.id);
      setQuickFilter(QuickFilterSlugs.CUSTOMER, 'true', {
        excludedBrands: updatedExcludedBrands,
        favouriteBrands: activeFavoriteBrands,
      });
    }
  };

  const onBrandClick = (brandId: string, brandSlug?: string) => {
    const isFavoriteBrand =
      favoriteBrandsFilterActive &&
      normalizedHighlightedBrands.map(brand => brand.id).includes(brandId);

    isFavoriteBrand
      ? handlePersonalizedBrandClick(brandId)
      : handleBrandClick(brandId, brandSlug);
  };

  const handleBrandClick = useCallback(
    (id: string, brandSlug?: string) => {
      setFilter?.({
        filterId: id,
        filterSection: slug,
        filterSlug: brandSlug,
      });
    },
    [setFilter, slug],
  );

  const handleFilterReset = () => {
    resetFilters?.([
      slug,
      QuickFilterSlugs.CUSTOMER,
      QuickFilterSlugs.BRANDS_EXCLUDED,
    ]);
  };

  return {
    activeSlugFilters,
    normalizedBrandGroups,
    excludedBrands,
    favoriteBrandsFilterActive,
    normalizedHighlightedBrands,
    highlightedBrandsLabel,
    highlightedBrandsType,
    onBrandClick,
    handleBrandClick,
    handleAllFavoriteBrandsToggle,
    handleSingleFavoriteBrandClick,
    handleFilterReset,
  };
};

interface NormalizedBrand {
  items: {
    count: number | undefined;
    id: string;
    slug: string;
    label: string;
    productCount: number | undefined;
    description?: string;
  }[];
  groupLabel: string;
  brands: AttributeFilterSection_AttributeValue[];
}

export const useNormalizedBrandGroups = (
  brandGroups: Array<BrandFilterSection_BrandGroup>,
  addPersonalisedBrands: boolean,
  highlightLabel?: string,
  highlightedBrands?: Array<HighlightBrand>,
): NormalizedBrand[] =>
  useMemo(() => {
    let result: NormalizedBrand[] = [];
    if (addPersonalisedBrands && highlightedBrands && highlightLabel) {
      result = [
        {
          groupLabel: highlightLabel,
          brands: [],
          items: highlightedBrands.map(brand => ({
            id: `${brand.id}`,
            label: brand.name,
            count: undefined,
            productCount: undefined,
            slug: brand.slug!,
          })),
        },
      ];
    }

    const normalizedGroups = brandGroups.map(brandGroup => ({
      groupLabel: brandGroup.groupLabel,
      brands: brandGroup.brands,
      items: brandGroup.brands.map(brand => ({
        id: `${brand.id}`,
        label: brand.label,
        count: brand.productCount,
        productCount: brand.productCount,
        slug: brand.slug,
        description: brand.description,
      })),
    }));

    return [...result, ...normalizedGroups];
  }, [brandGroups, addPersonalisedBrands, highlightLabel, highlightedBrands]);

const useNormalizedHighlightedBrands = (
  highlightedBrandsFilter?:
    | BrandFilterSection_PersonalizedBrandsFilter
    | BrandFilterSection_PerformingBrandsFilter,
): HighlightBrand[] =>
  useMemo(() => {
    if (!highlightedBrandsFilter || !highlightedBrandsFilter.brands) {
      return [];
    }
    return highlightedBrandsFilter.brands.map(brand => ({
      id: `${brand.id}`,
      img: getLogoUrl(brand.blackLogoImage?.url),
      name: brand.name,
      slug: brand.slug,
    }));
  }, [highlightedBrandsFilter]);

const getLogoUrl = (baseUrl?: string) => {
  if (!baseUrl) return undefined;

  return getAdjustedCdnUrl(baseUrl, {
    bg: COLORS.ProductImageBackground,
    brightness: 0.96,
    height: 60,
    quality: 75,
    trim: true,
    width: 210,
  });
};
